import type { PermissionModule } from "@core/session/types";

// Doce entradas, orden exacto de SPEC-201 §2. NO es un mapeo 1:1 con los
// siete módulos de permiso: varias entradas comparten módulo (SPEC-201 §2).
//
// Mapeo módulo → entrada: solo "circulo" tiene su módulo explícito en la SPEC
// (ANNEX-015 §2.4: mismo permiso que "miembros" → members). Para el resto,
// este archivo asigna el módulo por lectura razonable del dominio de cada
// pantalla (p. ej. "premios" → prizes, "catalogos" → catalogs). "recompensas"
// → content quedó confirmado al implementar SPEC-210 §7 ("contenido", con
// glossary.md:313/523 mapeando contenido → content) — ya no es la suposición
// que describía este comentario. "agenda" → content quedó confirmado al
// implementar SPEC-207 §"Permisos" (mismo módulo contra el que autorizan las
// rutas de `api/contracts/parts/60-portal-scheduling.yaml`). Esa asignación
// para plantillas/solicitudes/bienvenida/inicio → "content" y "solicitudes"
// → "members" sigue sin confirmar contra sus propias SPECs (SPEC-202, 211,
// 212, 214-217) cuando se implementen, no una decisión de negocio tomada
// acá.
//
// Íconos: las 10 entradas presentes en NAV del prototipo (admin.jsx:6-17),
// incluido Círculo, conservan su mismo nombre de ícono. Las dos entradas que
// SPEC-201 agrega y que no existen en ese NAV son "recompensas" y
// "plantillas": usan respectivamente "gift" y "chat", ya presentes en el
// lenguaje visual del prototipo.
export interface NavEntry {
  key: string;
  label: string;
  path: string;
  icon: string;
  permissionModule: PermissionModule;
  adminOnly?: boolean;
  children?: NavEntry[];
}

export const NAV: NavEntry[] = [
  {
    key: "bienvenida",
    label: "Pantalla de bienvenida",
    path: "/welcome-screen",
    icon: "star",
    permissionModule: "content",
  },
  {
    key: "miembros",
    label: "Gestión de miembros",
    path: "/members",
    icon: "invite",
    permissionModule: "members",
  },
  {
    key: "circulo",
    label: "Gestión del Círculo de Miembros",
    path: "/member-circle",
    icon: "link",
    permissionModule: "members",
  },
  {
    key: "inicio",
    label: "Pantalla de inicio",
    path: "/home-screen",
    icon: "home",
    permissionModule: "content",
  },
  {
    key: "agenda",
    label: "Agendamiento",
    path: "/scheduling",
    icon: "calendar",
    permissionModule: "content",
  },
  {
    key: "premios",
    label: "Gestión de Premios",
    path: "/prizes",
    icon: "medal",
    permissionModule: "prizes",
    children: [
      {
        key: "premios-config",
        label: "Configuración",
        path: "/prizes/configuration",
        icon: "medal",
        permissionModule: "prizes",
      },
      {
        key: "premios-avances",
        label: "Avances y referidos",
        path: "/prizes/progress",
        icon: "medal",
        permissionModule: "prizes",
      },
    ],
  },
  {
    key: "recompensas",
    label: "Recompensas, Beneficios y Ofertas",
    path: "/rewards",
    icon: "gift",
    permissionModule: "content",
  },
  {
    key: "catalogos",
    label: "Catálogos",
    path: "/catalogs",
    icon: "doc2",
    permissionModule: "catalogs",
  },
  {
    key: "motor",
    label: "Motor de Puntos",
    path: "/points-engine",
    icon: "sparkle",
    permissionModule: "points_engine",
  },
  {
    key: "plantillas",
    label: "Plantillas de mensaje",
    path: "/message-templates",
    icon: "chat",
    permissionModule: "content",
  },
  {
    key: "notificaciones",
    label: "Notificaciones push",
    path: "/notifications",
    icon: "bell",
    permissionModule: "content",
    adminOnly: true,
  },
  {
    key: "seguridad",
    label: "Seguridad",
    path: "/security",
    icon: "lock",
    permissionModule: "security",
    children: [
      {
        key: "seguridad-usuarios",
        label: "Usuarios",
        path: "/security/users",
        icon: "lock",
        permissionModule: "security",
      },
      {
        key: "seguridad-perfiles",
        label: "Perfiles",
        path: "/security/profiles",
        icon: "lock",
        permissionModule: "security",
      },
      {
        key: "seguridad-bitacora",
        label: "Bitácora",
        path: "/security/access-log",
        icon: "lock",
        permissionModule: "security",
      },
      // Visible solo para el Administrador (no cualquier perfil con permiso
      // sobre `security`): el gate real está en la pantalla y en el servidor
      // (`session.user.isAdministrator`, ADR-011). Se agrega al final para no
      // alterar a qué hijo redirige `FirstVisibleChildRedirect` para un
      // perfil no administrador con `security.view`.
      {
        key: "seguridad-traza-http",
        label: "Traza HTTP",
        path: "/security/http-request-log",
        icon: "lock",
        permissionModule: "security",
      },
    ],
  },
  {
    key: "solicitudes",
    label: "Solicitudes",
    path: "/requests",
    icon: "send",
    permissionModule: "members",
  },
  // SPEC-220 §3/ANNEX-010 §2.2: agregada a continuación de las 12 entradas de
  // SPEC-201 §2 — no reordena esa lista fija. Mismo criterio que
  // "solicitudes": entrada propia bajo el módulo `members`, sin ser hijo
  // anidado de "miembros" (el contrato visual fija la ruta plana
  // `/portal/redemptions`, no `/members/redemptions`).
  {
    key: "consumos",
    label: "Consumos",
    path: "/redemptions",
    icon: "qr",
    permissionModule: "members",
  },
];
